'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'

interface AnimatedWordsProps {
  text: string
  className?: string
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div'
  staggerDelay?: number
  animationDelay?: number
  duration?: number
}

export const AnimatedWords: React.FC<AnimatedWordsProps> = ({
  text,
  className = '',
  as = 'div',
  staggerDelay = 0.1,
  animationDelay = 0,
  duration = 0.75
}) => {
  const [inView, setInView] = useState(false)
  const [animate, setAnimate] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()

  // Intersection Observer
  useEffect(() => {
    const element = ref.current
    if (!element || isAdminMode) {
      if (isAdminMode) setAnimate(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          setInView(true)
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [isAdminMode])

  // Animation trigger: only after preloader is finished AND element is in view
  useEffect(() => {
    if (isAdminMode) {
      setAnimate(true)
      return
    }

    if (inView && preloaderState === 'hidden' && !isInitialLoad) {

      setAnimate(true)
    }
  }, [isAdminMode, inView, preloaderState, isInitialLoad, text])

  // Framer Motion variant object for controlling word animations
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: animationDelay
      }
    }
  }

  const wordVariants = {
    hidden: {
      y: "100%",
      transition: { 
        ease: [0.455, 0.03, 0.515, 0.955] as [number, number, number, number], 
        duration: duration 
      }
    },
    visible: {
      y: 0,
      transition: { 
        ease: [0.455, 0.03, 0.515, 0.955] as [number, number, number, number], 
        duration: duration 
      }
    }
  }

  // Parse HTML content to extract text and preserve spans and line breaks
  const parseHTMLContent = (htmlString: string) => {
    // Simple regex-based parsing for spans and br tags to avoid DOM manipulation
    const words: Array<{ content: string; isSpan: boolean; className?: string; isLineBreak?: boolean }> = []
    
    // First, split by br tags and process each line
    const lines = htmlString.split(/(<br\s*\/?>)/gi)
    
    lines.forEach((line) => {
      if (line.match(/<br\s*\/?>/gi)) {
        // This is a line break
        words.push({ content: '', isSpan: false, isLineBreak: true })
      } else if (line.trim()) {
        // Split by span tags and process each part
        const parts = line.split(/(<span[^>]*>.*?<\/span>)/gi)
        
        parts.forEach(part => {
          if (part.match(/<span[^>]*>.*?<\/span>/gi)) {
            // This is a span element
            const classMatch = part.match(/class=['"]([^'"]*)['"]/i)
            const textMatch = part.match(/<span[^>]*>(.*?)<\/span>/i)
            
            if (textMatch && textMatch[1]) {
              const spanWords = textMatch[1].split(/\s+/).filter(word => word.trim() !== '')
              spanWords.forEach(word => {
                words.push({
                  content: word,
                  isSpan: true,
                  className: classMatch ? classMatch[1] : ''
                })
              })
            }
          } else if (part.trim()) {
            // This is regular text
            const textWords = part.split(/\s+/).filter(word => word.trim() !== '')
            textWords.forEach(word => {
              words.push({ content: word, isSpan: false })
            })
          }
        })
      }
    })
    
    return words
  }

  // In admin mode, render without animation
  if (isAdminMode) {
    const Component = as as React.ElementType
    return (
      <Component 
        ref={ref}
        className={className}
        dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(text) }}
      />
    )
  }

  // Parse the HTML content
  const words = parseHTMLContent(text)
  const Component = motion[as as keyof typeof motion] as React.ComponentType<Record<string, unknown>>

  return (
    <Component
      ref={ref}
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate={animate ? "visible" : "hidden"}
    >
      {words.map((word, index) => {
        if (word.isLineBreak) {
          return <br key={index} />
        }
        
        return (
          <span
            key={index}
            style={{
              overflow: "hidden",
              display: "inline-block"
            }}
          >
            <motion.span
              style={{ 
                display: "inline-block"
              }}
              variants={wordVariants}
            >
              {word.isSpan ? (
                <span className={word.className}>
                  {word.content}
                </span>
              ) : (
                word.content
              )}
            </motion.span>
            {index < words.length - 1 && !words[index + 1]?.isLineBreak && ' '}
          </span>
        )
      })}
    </Component>
  )
}
